/**
 * `growth` — portfolio ADS kit (canon: listto). Contract + model.
 *
 * Separation of responsibilities (the one that stops each app from reinventing):
 *   - **`AdProvider`** (this layer, `provider.ts`) decides *whether it CAN*: environment
 *     flags, user tier, platform, network available. It is the only thing that
 *     talks to an SDK.
 *   - **`adGate`** (`gate.ts`) decides *whether it is DUE*: breakpoints, frequency capping
 *     and minors policy. A PURE function, without SDK or its own clock.
 *   - **`AdCapsStore`** (`caps.ts`) keeps the counters **local-first**
 *     (`ads.cap.<placement>.<day>`): works offline and for guests.
 *
 * 100% DOMAIN-AGNOSTIC kit: it does not know what a list, a habit or a
 * lesson is. Each app brings its own placement registry and its rewards.
 *
 * **OFF by default**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the whole layer is
 * inert and NO SDK is loaded (zero network requests — the privacy
 * promise is technical, not cosmetic).
 */

/** Supported formats, in portfolio preference order. */
export const AD_KINDS = ["rewarded", "native", "interstitial", "banner"] as const;

export type AdKind = (typeof AD_KINDS)[number];

/** Breather moments where an ad is legal. */
export const AD_BREAKPOINTS = [
  "done-screen",
  "session-end",
  "between-screens",
  "stats",
  "feed-slot",
  "reward-moment",
] as const;

export type AdBreakpointId = (typeof AD_BREAKPOINTS)[number];

/** App ad tier (portfolio classification). */
export const AD_TIERS = ["A", "K", "C", "D"] as const;

/**
 * - `A` — consumer: all formats.
 * - `K` — kids: COPPA/Families regime (banner G + soft interstitial; the
 *   rewarded requires an adult gate).
 * - `C` — B2B/institutional: ZERO ads.
 * - `D` — privacy: ZERO ads and zero embedded SDK.
 */
export type AdTier = (typeof AD_TIERS)[number];

/**
 * Content ratings, **ordered from strictest to laxest**. The order IS
 * the rank: `strictestAdContentRating` relies on the index.
 */
export const AD_CONTENT_RATINGS = ["G", "PG", "T", "MA"] as const;

export type AdContentRating = (typeof AD_CONTENT_RATINGS)[number];

/**
 * **Tier's policy floor.** It is what the clamp (`clampAdsConfigToTier`)
 * guarantees no matter what: neither an env, nor a back-office override, nor the
 * options of an `init()` call can go below here.
 */
export interface AdTierRegime {
  /** `false` ⇒ neither ads nor embedded SDK, even if the flag is on. */
  readonly adsAllowed: boolean;
  /** `true` ⇒ forced minors regime (COPPA / Play Families). */
  readonly childDirected: boolean;
  /** Tier rating ceiling. */
  readonly maxAdContentRating: AdContentRating;
}

/**
 * Regime of an UNKNOWN tier: the most restrictive that exists (fail-closed).
 * Without a declared policy no advertising is served — and if it were, it would be in
 * child regime with rating G.
 */
export const STRICTEST_AD_TIER_REGIME: AdTierRegime = {
  adsAllowed: false,
  childDirected: true,
  maxAdContentRating: "G",
};

/**
 * Declared regime of each tier (§9 of the ads strategy). It is a table, not
 * a chain of `if`s, so that adding a tier forces declaring its floor.
 */
export const AD_TIER_REGIMES: Readonly<Record<AdTier, AdTierRegime>> = {
  A: { adsAllowed: true, childDirected: false, maxAdContentRating: "MA" },
  K: { adsAllowed: true, childDirected: true, maxAdContentRating: "G" },
  C: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
  D: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
};

/** Reward of a rewarded: what the user gains, visible BEFORE seeing it. */
export interface AdReward {
  /** Stable reward type id (`streak-freeze`, `theme-unlock`, …). */
  type: string;
  amount: number;
  /** Validity of temporary rewards, in hours. Absent = consumable. */
  durationHours?: number;
}

/** Declaration of a placement in the app's typed registry. */
export interface PlacementDef {
  kind: AdKind;
  breakpoint: AdBreakpointId;
  /** Only for `rewarded`: what is redeemed. */
  reward?: AdReward;
}

/** Placement registry of an app (`Record<id, PlacementDef>`). */
export type PlacementRegistry = Record<string, PlacementDef>;

/** Result of initializing the provider (COPPA flags included). */
export interface AdInitOptions {
  childDirected?: boolean;
  maxAdContentRating?: AdContentRating;
  testDeviceIds?: string[];
}

/** Result of showing an ad. `reward` ONLY if the user completed it. */
export interface AdShowResult {
  shown: boolean;
  reward?: AdReward;
}

export type AdEventName = "impression" | "reward" | "dismiss" | "error";

export interface AdEvent {
  kind: AdKind;
  placementId: string;
  reward?: AdReward;
  error?: string;
}

/**
 * Ad provider contract. It is implemented by one backend per network
 * (`none` = no-op and DEFAULT, `adsense` = web). The placement layer only talks
 * to this interface → changing network does not touch a single screen.
 */
export interface AdProvider {
  /** Stable backend id (`none`, `adsense`, `admob`, …). */
  readonly id: string;
  /** Initializes the network. Idempotent. With `none` it does NOTHING (not even a request). */
  init(opts?: AdInitOptions): Promise<void>;
  /** `false` ⇒ the whole placement layer collapses for that format. */
  isEnabled(kind: AdKind): boolean;
  /** Best-effort preload. `false` = no fill ⇒ the UI collapses, never a spinner. */
  prepare(kind: AdKind, placementId: string): Promise<boolean>;
  /** Shows the ad. For `rewarded`, resolves with the reward if it was completed. */
  show(kind: AdKind, placementId: string): Promise<AdShowResult>;
  /** Event subscription. Returns the unsubscribe function. */
  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void;
}

/** Resolved ads configuration (env → admin UI overrides). */
export interface AdsConfig {
  /** Master switch. **false by default**. */
  enabled: boolean;
  /** Provider backend. `none` by default (no-op, no SDK). */
  network: string;
  /** App ad tier (§9 of the strategy). */
  tier: AdTier;
  /** Minors regime: forces the strict policy in the gate. */
  childDirected: boolean;
  /** Maximum rating of the served content. */
  maxAdContentRating: AdContentRating;
  /** Client id of the web network (e.g. `ca-pub-…`). Empty ⇒ no fill. */
  clientId: string;
}

/** Local day key `YYYY-MM-DD` (the caller decides the timezone). */
export type AdDayKey = string;

/**
 * Capping counters. They live **locally** (device) under
 * `ads.cap.<placement>.<day>` → they work offline and without an account.
 */
export interface CapsState {
  /** Day the daily counters belong to. */
  day: AdDayKey;
  /** Interstitials shown today (all placements). */
  interstitialsToday: number;
  /** Interstitials shown in the current session. */
  interstitialsThisSession: number;
  /** Epoch ms of the last INTERRUPTIVE ad shown. */
  lastInterstitialAt?: number;
  /** Rewards granted today, by `reward.type` (anti-farming). */
  rewardsToday: Record<string, number>;
  /** Rewards granted today, by PLACEMENT (feeds the backoff). */
  takenToday: Record<string, number>;
  /** Times the rewarded offer was shown today, per placement. */
  offersToday: Record<string, number>;
  /** Consecutive days with the offer shown and NO reward (backoff). */
  ignoredDays: Record<string, number>;
  /** Natives already rendered on the current screen. */
  nativesOnScreen: number;
  /** Banners visible right now. */
  bannersVisible: number;
}

/** Signals of the ongoing session (provided by the app-shell). */
export interface AdSessionState {
  /** ACTIVE usage time accumulated in the session, in ms. */
  activeMs: number;
  /** Value units completed in the session (closed lists, …). */
  unitsCompleted: number;
  /** Is it the user's very first session? (clean onboarding). */
  isFirstEver: boolean;
}

export interface AdUserState {
  /** Ad-free tier (resolved by `shouldShowAds` of the monetization kit). */
  premium: boolean;
  /** Surface used by a minor ⇒ §6 regime. */
  child: boolean;
  /** Adult gate cleared (the only path to rewarded in child regime). */
  adultGateCleared?: boolean;
}

export interface AdGateInput {
  kind: AdKind;
  placementId: string;
  /** Declared breakpoint of the placement (for the minors policy). */
  breakpoint?: AdBreakpointId;
  /** Reward type, only `rewarded` (anti-farming by type). */
  rewardType?: string;
  /** Epoch ms. Injected: the gate is PURE. */
  now: number;
  caps: CapsState;
  session: AdSessionState;
  user: AdUserState;
  /** Active input / timer running / transaction half-done ⇒ there is NEVER an ad. */
  inFlight: boolean;
  /** Numeric policy; portfolio's by default (§4). */
  policy?: Partial<AdCapPolicy>;
}

/** Stable reasons — they feed telemetry and tests (they are not UI texts). */
export type AdGateReason =
  | "allow"
  | "premium"
  | "in-flight"
  | "child-policy"
  | "first-session"
  | "no-value-unit"
  | "session-cap"
  | "daily-cap"
  | "min-gap"
  | "interruption-budget"
  | "reward-daily-cap"
  | "offer-daily-cap"
  | "offer-backoff"
  | "screen-cap";

export interface AdGateDecision {
  allow: boolean;
  reason: AdGateReason;
}

/** Numeric capping policy (portfolio defaults in `gate.ts`). */
export interface AdCapPolicy {
  interstitialPerSession: number;
  interstitialPerDay: number;
  /** Minimum distance between interstitials, in ms of usage. */
  minGapBetweenInterstitialsMs: number;
  /** Is the first interstitial allowed in the 1st session ever? */
  allowInterstitialOnFirstSession: boolean;
  /** Value units the session must have before an interstitial. */
  minUnitsCompleted: number;
  /** Maximum rewards per day PER TYPE (anti-farming). */
  rewardsPerDayPerType: number;
  /** Times an ignored rewarded is offered, per day and per placement. */
  offersPerDayPerPlacement: number;
  /** Consecutive days ignoring the offer before lowering its frequency. */
  ignoredDaysBeforeBackoff: number;
  /** Reduced frequency (offers/day) once in backoff. */
  offersPerDayWhenBackedOff: number;
  nativePerScreen: number;
  bannersVisible: number;
  /** Global budget: 1 interruptive impression per every X ms of active usage. */
  interruptionBudgetMs: number;
}
